// frontend/src/services/api.ts
import { DashboardData, Session, TeamPointsData } from "@/types/models";
import { PlayerDTO } from "../../../dto/playerDTO";
import {
  CreateLeagueRequest,
  LeagueDTO,
  LeagueInviteDTO,
  LeagueRoleDTO,
  LeaveLeagueResultDTO,
} from "../../../dto/leagueDTO";
import {
  RawNotification,
  deserializeNotification,
} from "../../../dto/notificationDTO";
import { TeamDTO } from "../../../dto/teamDTO";
import { ContractDTO, type RawContract } from "../../../dto/contractDTO";
import { ArticleDTO } from "../../../dto/articleDTO";
import { PerformanceDTO } from "../../../dto/performanceDTO";
import { LeaderboardEntryDTO } from "../../../dto/leaderboardDTO";
import type { WikipediaEditionDTO } from "../../../dto/wikipediaEditionDTO";
import type {
  CreateProblemReportRequest,
  ProblemReportCreatedDTO,
} from "../../../dto/problemReportDTO";
import type {
  GenieSeedRequest,
  GenieSeedResponse,
  GenieTurnRequest,
  GenieTurnResponse,
} from "../../../dto/genieDTO";
import { Temporal } from "@js-temporal/polyfill";
import { normalizeLanguageScale } from "../../../model/languageScale";
import {
  TIER_DAYS,
  computeCurrentPrice,
  type ContractTier,
} from "../../../model/pricing";
import { createWikimediaClient } from "@/services/wikimediaClient";
import type { SessionDTO, SessionFeaturesDTO } from "../../../dto/sessionDTO";
import type {
  PasswordCredentialsRequest,
  PasswordSessionDTO,
} from "../../../dto/passwordAuthDTO";

// The major version lives in the path segment: a future v2 can be served
// alongside v1 on the backend while this constant is the one line that moves
// clients over (backend/openapi.yaml carries the same rule).
export const API_BASE_URL = "/api/v1";

/**
 * Carries the HTTP status and the backend's error code alongside the message.
 * Callers that only read `.message` are unaffected; the report form needs the
 * status to tell "you just sent one" (429) apart from "GitHub is down" (502),
 * which lead to very different offers to the user.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Network error" }));
    throw new ApiError(
      error.error || `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      error.error
    );
  }

  return response.json();
}

export function deserializeLeague(l: LeagueDTO): LeagueDTO {
  return {
    ...l,
    // Guarded rather than trusted, because this also parses the league restored
    // from `localStorage` on boot — which may have been saved before the field
    // existed. An absent factor would multiply out to NaN and show every article
    // in the market priced at 0 until the fetch landed.
    languageScale: normalizeLanguageScale(l.languageScale),
    startDate: Temporal.Instant.from(l.startDate as unknown as string),
    endDate: Temporal.Instant.from(l.endDate as unknown as string),
    // Null for every open league, which is most of them — so this is the one
    // date on the shape that has to survive the wire as an absence.
    closedAt:
      l.closedAt === null || l.closedAt === undefined
        ? null
        : Temporal.Instant.from(l.closedAt as unknown as string),
  };
}

// ── Player ────────────────────────────────────────────────────────────────────

export const playerApi = {
  getCurrent: () => apiRequest<PlayerDTO>("/player"),
  getTeams: () => apiRequest<TeamDTO[]>("/player/teams"),
  getNotifications: () =>
    apiRequest<RawNotification[]>("/player/notifications").then((ns) =>
      ns.map(deserializeNotification)
    ),
};

// ── Leagues ───────────────────────────────────────────────────────────────────

export const leaguesApi = {
  /** The current player's leagues (resolved from JWT on backend) */
  getAll: () =>
    apiRequest<LeagueDTO[]>("/leagues").then((ls) => ls.map(deserializeLeague)),
  getById: (id: string) =>
    apiRequest<LeagueDTO>(`/leagues/${id}`).then(deserializeLeague),
  /**
   * The league an invitation code opens, so the player can see what they are
   * being invited to before naming a team in it.
   *
   * Every rejection is one 404 with one body — a wrong code, an unused code and
   * a malformed one are indistinguishable on purpose (ADR 0008), so there is
   * nothing here for the caller to branch on beyond "this did not resolve".
   * The endpoint is rate limited and answers 429 when the player has spent
   * their attempts; `ApiError.status` is what tells the two apart.
   */
  getByCode: (code: string) =>
    apiRequest<LeagueDTO>(`/leagues/by-code/${encodeURIComponent(code)}`).then(
      deserializeLeague
    ),
  /**
   * Found a league. The founder is resolved from the session, and the founding
   * team named in the request is created with it in one transaction.
   */
  create: (request: CreateLeagueRequest) =>
    apiRequest<LeagueDTO>("/leagues", {
      method: "POST",
      body: JSON.stringify(request),
    }).then(deserializeLeague),
  /**
   * A league's invitation code. Only a private league has one — a public league
   * answers 404, by design — so call it on the strength of `visibility`, not
   * hopefully.
   */
  getInviteCode: (id: string) =>
    apiRequest<LeagueInviteDTO>(`/leagues/${id}/invite-code`),
  /**
   * What the caller is in this league — member, admin, both or neither. Kept
   * off `LeagueDTO` because that shape is the same answer for everyone.
   */
  getMyRole: (id: string) =>
    apiRequest<LeagueRoleDTO>(`/leagues/${id}/my-role`),
  /**
   * Every public league, newest first. Not caller-scoped — the leagues the
   * player already plays are dropped where they are rendered.
   */
  getPublic: () =>
    apiRequest<LeagueDTO[]>("/leagues/public").then((ls) =>
      ls.map(deserializeLeague)
    ),
  /** The Global League, joinable by any player regardless of locale */
  getGlobal: () =>
    apiRequest<LeagueDTO>("/leagues/global").then(deserializeLeague),
  /**
   * Close a league early, which only its admin may do. A `POST` on a noun and
   * not a `DELETE`, because nothing is removed: the league, its teams and its
   * final table stay readable, and the response is the league itself with
   * `closedAt` now set (docs/domain/league-lifecycle.md).
   */
  close: (id: string) =>
    apiRequest<LeagueDTO>(`/leagues/${id}/closure`, { method: "POST" }).then(
      deserializeLeague
    ),
  /**
   * Leave a league. The caller's team keeps its contracts and its place in the
   * standings; what ends is their part in it. Final — the join gate refuses a
   * player who has left.
   */
  leave: (id: string) =>
    apiRequest<LeaveLeagueResultDTO>(`/leagues/${id}/my-departure`, {
      method: "POST",
    }),
  /** The current player's team inside this league (resolved from JWT on backend) */
  getMyTeam: (id: string) => apiRequest<TeamDTO>(`/leagues/${id}/my-team`),
  /**
   * Create the current player's team inside this league — which is what joining
   * a league *is* (resolved from JWT on backend).
   *
   * `invitationCode` is only meaningful for a private league; a public one
   * ignores it. Left out entirely when absent: `JSON.stringify` drops an
   * `undefined` value, so signup and the public-league shelf send the same body
   * they always did — which also keeps them out of the redeem rate limit, since
   * the backend only charges a request that presents a code.
   */
  createMyTeam: (id: string, name: string, invitationCode?: string) =>
    apiRequest<TeamDTO>(`/leagues/${id}/my-team`, {
      method: "POST",
      body: JSON.stringify({ name, invitationCode }),
    }),
  /** All contracts of the current player's team in this league */
  getMyContracts: (id: string) =>
    apiRequest<RawContract[]>(`/leagues/${id}/my-contracts`).then((cs) =>
      cs.map((c) => ContractDTO.fromRaw(c))
    ),
  /** All contracts held by any team in this league (used by the Market view) */
  getContracts: (id: string) =>
    apiRequest<RawContract[]>(`/leagues/${id}/contracts`).then((cs) =>
      cs.map((c) => ContractDTO.fromRaw(c))
    ),
  /** All notifications for the current player in this league */
  getMyNotifications: (id: string) =>
    apiRequest<RawNotification[]>(`/leagues/${id}/my-notifications`).then(
      (ns) => ns.map(deserializeNotification)
    ),

  // ── Leaderboard ────────────────────────────────────────────────────────────

  getLeaderboard: (id: string) =>
    apiRequest<LeaderboardEntryDTO[]>(`/leagues/${id}/leaderboard`),

  // ── Performance ────────────────────────────────────────────────────────────

  async getRecentPoints(id: string): Promise<TeamPointsData> {
    const [latest, previous] = await apiRequest<PerformanceDTO[]>(
      `/leagues/${id}/my-performances?limit=2`
    );
    const latestPts = latest?.points ?? 0;
    const previousPts = previous?.points ?? 0;
    return {
      // Round to one decimal for display; the raw value carries full float
      // precision from the scoring engine.
      yesterdayPoints: Math.round(latestPts * 10) / 10,
      pointsChange:
        previousPts > 0
          ? Math.round(((latestPts - previousPts) / previousPts) * 100)
          : 0,
    };
  },

  /** Buy a contract for the current player's team in this league */
  buyMyContract: (leagueId: string, articleId: string, tier: ContractTier) =>
    apiRequest<RawContract>(`/leagues/${leagueId}/my-contracts`, {
      method: "POST",
      body: JSON.stringify({ articleId, tier }),
    }).then((c) => ContractDTO.fromRaw(c)),

  /** Sell one of the current player's contracts early for a prorated payout */
  sellMyContract: (leagueId: string, contractId: string) =>
    apiRequest<RawContract>(
      `/leagues/${leagueId}/my-contracts/${contractId}/sell`,
      { method: "POST" }
    ).then((c) => ContractDTO.fromRaw(c)),

  /**
   * Elect to renew one of the current player's contracts at expiry (only valid
   * in the final-24h window). Sets the renewal flag; the daily settlement sweep
   * executes the roll-forward at expiry.
   */
  renewMyContract: (leagueId: string, contractId: string) =>
    apiRequest<RawContract>(
      `/leagues/${leagueId}/my-contracts/${contractId}/renew`,
      { method: "POST" }
    ).then((c) => ContractDTO.fromRaw(c)),

  /**
   * Withdraw a renewal election. Valid until the settlement sweep executes the
   * renewal at expiry — the election is only an intent until then.
   */
  cancelRenewMyContract: (leagueId: string, contractId: string) =>
    apiRequest<RawContract>(
      `/leagues/${leagueId}/my-contracts/${contractId}/renew`,
      { method: "DELETE" }
    ).then((c) => ContractDTO.fromRaw(c)),
};

// ── Teams ─────────────────────────────────────────────────────────────────────

export const teamsApi = {
  getById: (id: string) => apiRequest<TeamDTO>(`/teams/${id}`),
  getContracts: (id: string) =>
    apiRequest<RawContract[]>(`/teams/${id}/contracts`).then((cs) =>
      cs.map((c) => ContractDTO.fromRaw(c))
    ),
};

// ── Contracts ─────────────────────────────────────────────────────────────────

export const contractsApi = {
  getById: (id: string) =>
    apiRequest<RawContract>(`/contracts/${id}`).then((c) =>
      ContractDTO.fromRaw(c)
    ),
  delete: (id: string) =>
    apiRequest<{ message: string; refundedCredits: number }>(
      `/contracts/${id}`,
      { method: "DELETE" }
    ),
};

// ── Notifications ─────────────────────────────────────────────────────────────

export const notificationsApi = {
  getAll: () =>
    apiRequest<RawNotification[]>("/notifications").then((ns) =>
      ns.map(deserializeNotification)
    ),
  markAsRead: (id: string) =>
    apiRequest<{ success: boolean }>(`/notifications/${id}/read`, {
      method: "PATCH",
    }),
};

// ── Articles ──────────────────────────────────────────────────────────────────

export const articlesApi = {
  getAll: () => apiRequest<ArticleDTO[]>("/articles"),
  getById: (id: string) => apiRequest<ArticleDTO>(`/articles/${id}`),
};

const wikimediaClient = createWikimediaClient();

/**
 * ContractPrice (ADR 0005) at the contract's own held tier — neither
 * ContractDTO nor ArticleDTO carry pageview data, so it's fetched live per
 * article (same source as ArticleDetail's stats block). If Wikimedia has no
 * usable view history (a failed/empty fetch — `getArticleViews` swallows
 * errors into `undefined` fields rather than throwing), fall back to
 * purchasePrice: a stale-but-plausible figure beats silently pricing the
 * contract at 0, which would understate the portfolio total for reasons
 * invisible to the viewer.
 */
export async function getContractCurrentPrice(
  contract: ContractDTO,
  languageScale: number
): Promise<number> {
  const views = await wikimediaClient.pageviews.getArticleViews(
    contract.article.domain,
    contract.article.title
  );
  if (views.averageViews30d === undefined) {
    return contract.purchasePrice;
  }
  return computeCurrentPrice(
    views.averageViews30d,
    languageScale,
    TIER_DAYS[contract.tier as ContractTier]
  );
}

export const dashboardApi = {
  async getDashboardData(league: LeagueDTO): Promise<DashboardData> {
    const [team, contracts, notifications, recentPoints, leaderboard] =
      await Promise.all([
        leaguesApi.getMyTeam(league.id),
        leaguesApi.getMyContracts(league.id),
        leaguesApi.getMyNotifications(league.id),
        leaguesApi.getRecentPoints(league.id),
        leaguesApi.getLeaderboard(league.id),
      ]);

    const rank = leaderboard.find((e) => e.team.id === team.id)?.rank ?? 0;
    const totalPlayers = leaderboard.length;
    const currentPrices = await Promise.all(
      contracts.map((contract) =>
        getContractCurrentPrice(contract, league.languageScale)
      )
    );
    const portfolioValue = currentPrices.reduce((sum, p) => sum + p, 0);

    return new DashboardData(
      team,
      league,
      contracts,
      notifications,
      recentPoints,
      rank,
      totalPlayers,
      portfolioValue
    );
  },
};

// ── Session ───────────────────────────────────────────────────────────────────

/**
 * Every optional feature defaults to off. A response without a `features` block
 * came from a backend that predates the flag, and hiding a feature that turns
 * out to be there costs a player nothing next to offering one that isn't.
 */
function readFeatures(
  raw: Partial<SessionFeaturesDTO> | undefined
): SessionFeaturesDTO {
  return { articleGenie: raw?.articleGenie ?? false };
}

/** What actually arrives: an older backend sends no `features` at all. */
type RawSession = Omit<SessionDTO, "features"> & {
  features?: Partial<SessionFeaturesDTO>;
};

export const sessionApi = {
  get: async (): Promise<Session> => {
    const raw = await apiRequest<RawSession>("/session");
    return { ...raw, features: readFeatures(raw.features) };
  },
  delete: () =>
    apiRequest<{ success: boolean }>("/session", { method: "DELETE" }),
};

/**
 * Username/password sign-in, which only a backend built from
 * `src/indexPassword.ts` serves — the deployed Worker does not contain these
 * routes (docs/architecture/auth-modes.md). The form that calls this is gated
 * on `VITE_PASSWORD_AUTH`.
 *
 * Its own fetch rather than `apiRequest`, which prefixes `/api/v1` to everything
 * it is given: these live under `/auth`, beside the Google entry point. The
 * session arrives the same way it does from Google — as the `session_token`
 * cookie — so the caller goes on to `sessionApi.get()` exactly as
 * `AuthCallbackPage` does.
 */
async function passwordAuthRequest(
  endpoint: string,
  body: PasswordCredentialsRequest
): Promise<PasswordSessionDTO> {
  const response = await fetch(`/auth/password${endpoint}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Network error" }));
    throw new ApiError(
      error.error || `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      error.error
    );
  }

  return response.json();
}

export const passwordAuthApi = {
  register: (credentials: PasswordCredentialsRequest) =>
    passwordAuthRequest("/register", credentials),
  login: (credentials: PasswordCredentialsRequest) =>
    passwordAuthRequest("/login", credentials),
};

// ── Problem reports ───────────────────────────────────────────────────────────

/**
 * The Wikipedia editions a league can be founded on.
 *
 * Unscoped and the same for everyone, so it is fetched once and cached for the
 * session — it changes when a scheduled screen changes it, which is daily at
 * most (#531).
 */
export const wikipediaEditionsApi = {
  getAll: () => apiRequest<WikipediaEditionDTO[]>("/wikipedia-editions"),
};

export const reportsApi = {
  // The reporter is never sent: the backend resolves them from the session.
  create: (request: CreateProblemReportRequest) =>
    apiRequest<ProblemReportCreatedDTO>("/reports", {
      method: "POST",
      body: JSON.stringify(request),
    }),
};

// ── Article Genie ─────────────────────────────────────────────────────────────

/**
 * The Genie's two model calls. Everything else it does — searching Wikipedia,
 * fetching link sets, pricing — happens in the browser against the Wikimedia
 * client's own cache, where neither the Worker's subrequest nor its CPU limit
 * applies (ADR 0006).
 */
export const genieApi = {
  /** Reads the opening text into the searches that seed the candidate set. */
  seed: (query: string) =>
    apiRequest<GenieSeedResponse>("/me/genie-seeds", {
      method: "POST",
      body: JSON.stringify({ query } satisfies GenieSeedRequest),
    }),
  takeTurn: (request: GenieTurnRequest) =>
    apiRequest<GenieTurnResponse>("/me/genie-turns", {
      method: "POST",
      body: JSON.stringify(request),
    }),
};

// ── Unified export ────────────────────────────────────────────────────────────

export const api = {
  player: playerApi,
  leagues: leaguesApi,
  teams: teamsApi,
  contracts: contractsApi,
  notifications: notificationsApi,
  articles: articlesApi,
  dashboard: dashboardApi,
  session: sessionApi,
  reports: reportsApi,
  genie: genieApi,
  wikipediaEditions: wikipediaEditionsApi,
};

export default api;
